<template>
  <Teleport to="#app">
    <div class="toast-notification-container">
      <component
        :is="notification.component"
        v-for="notification in notifications"
        :key="notification.toastNotificationId"
        :params="notification.componentAttrs"
        v-bind="$attrs"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import RandomUtils from "@utils/random.utils";
import ToastEventBus from "@plugins/toast/service/event-bus.ts";
import { ref, onMounted } from "vue";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";

// Data
const notifications = ref([]);
const delayQueue = ref([]);
const isQueueProcessing = ref(false);

const { trackWatch } = useEventBusWatch();

// Methods
const add = function (component, componentAttrs = {}) {
  const PREFIX = "notification_";
  const toastNotificationId = PREFIX + RandomUtils.randomId();
  componentAttrs.toastNotificationId = toastNotificationId;

  const { delay } = componentAttrs;
  const notification = { toastNotificationId, component, componentAttrs };
  if (!delay) {
    notifications.value.push(notification);
  } else {
    queueDelayedNotification({ notification, delay, cancelled: false });
  }

  return toastNotificationId;
};

const queueDelayedNotification = function (delayedNotification) {
  delayQueue.value.push(delayedNotification);
  if (!isQueueProcessing.value) {
    processDelayQueue();
  }
};

const processDelayQueue = function () {
  isQueueProcessing.value = true;
  if (delayQueue.value?.length === 0) {
    isQueueProcessing.value = false;
    return;
  }
  const delay = (delayQueue.value[0] && delayQueue.value[0].delay) ?? 0;
  setTimeout(() => {
    const { notification, cancelled } = delayQueue.value.shift();
    if (notification && !cancelled) {
      notifications.value.push(notification);
    }
    processDelayQueue();
  }, delay);
};

const remove = function (toastNotificationId) {
  const index = notifications.value.findIndex((v) => v.toastNotificationId === toastNotificationId);
  if (index !== -1) {
    notifications.value.splice(index, 1);
    return;
  }
  const delayedNotification = delayQueue.value.find(
    (v) => v.notification?.toastNotificationId === toastNotificationId,
  );
  if (delayedNotification) {
    delayedNotification.cancelled = true;
  }
};

const removeAll = function () {
  notifications.value = [];
  clearDelayQueue();
};

const clearDelayQueue = function () {
  delayQueue.value = [];
};

defineExpose({
  add,
  remove,
  removeAll,
});

// Mounted
onMounted(() => {
  trackWatch(
    ToastEventBus.$on("remove-notification", (id) => {
      remove(id);
    }),
  );
});
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";

.toast-notification-container {
  position: fixed;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  left: 50%;
  margin-left: -300px;
  top: $app-header-height;
  z-index: z-map("toast-notification");

  @include media("mobile") {
    width: 100%;
    left: 0;
    margin-left: 0;
  }
}
</style>
